import { useCallback, useRef, useState } from "react";

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3;

export const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

/**
 * Pan and zoom for an endless canvas.
 *
 * Zoom is anchored on the pointer, which is the whole trick: without it,
 * zooming walks the board away from whatever the teacher was looking at. The
 * viewport is `{ x, y, zoom }` where x/y are the screen offset of board
 * origin, so converting between the two is one line each way.
 */
export function useViewport(initial = { x: 0, y: 0, zoom: 1 }) {
  const [viewport, setViewport] = useState(initial);
  const panOrigin = useRef(null);

  const toBoard = useCallback(
    (screenX, screenY) => ({
      x: (screenX - viewport.x) / viewport.zoom,
      y: (screenY - viewport.y) / viewport.zoom,
    }),
    [viewport]
  );

  /** Keep the board point under the cursor fixed while the scale changes. */
  const zoomAt = useCallback((factor, screenX, screenY) => {
    setViewport((v) => {
      const zoom = clampZoom(v.zoom * factor);
      if (zoom === v.zoom) return v;
      const scale = zoom / v.zoom;
      return {
        zoom,
        x: screenX - (screenX - v.x) * scale,
        y: screenY - (screenY - v.y) * scale,
      };
    });
  }, []);

  const zoomTo = useCallback((zoom, centre) => {
    setViewport((v) => {
      const next = clampZoom(zoom);
      if (!centre) return { ...v, zoom: next };
      const scale = next / v.zoom;
      return {
        zoom: next,
        x: centre.x - (centre.x - v.x) * scale,
        y: centre.y - (centre.y - v.y) * scale,
      };
    });
  }, []);

  /**
   * Step the zoom relative to whatever it currently is, around a point on
   * screen — the middle of the canvas, for the buttons and keys, so what you
   * are looking at stays where it is instead of sliding off to the top left.
   * Functional, so two quick clicks both count.
   */
  const zoomBy = useCallback((factor, centre) => {
    setViewport((v) => {
      const zoom = clampZoom(v.zoom * factor);
      if (!centre) return { ...v, zoom };
      const scale = zoom / v.zoom;
      return {
        zoom,
        x: centre.x - (centre.x - v.x) * scale,
        y: centre.y - (centre.y - v.y) * scale,
      };
    });
  }, []);

  /** Move the board by a screen distance — a trackpad scroll. */
  const panBy = useCallback((dx, dy) => {
    setViewport((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
  }, []);

  /**
   * Frame a region of the board: centred, as large as fits with some air
   * round it, never zoomed in past 100% — a single note should not fill the
   * screen. `size` is the canvas's width and height on screen.
   */
  const fitTo = useCallback((bounds, size, padding = 96) => {
    if (!bounds || bounds.width <= 0 || bounds.height <= 0 || !size) return;
    const zoom = clampZoom(
      Math.min(
        1,
        (size.width - padding * 2) / bounds.width,
        (size.height - padding * 2) / bounds.height
      )
    );
    setViewport({
      zoom,
      x: size.width / 2 - (bounds.x + bounds.width / 2) * zoom,
      y: size.height / 2 - (bounds.y + bounds.height / 2) * zoom,
    });
  }, []);

  const reset = useCallback(() => setViewport({ x: 0, y: 0, zoom: 1 }), []);

  const startPan = useCallback(
    (e) => {
      panOrigin.current = { px: e.clientX, py: e.clientY, x: viewport.x, y: viewport.y };
      // Held on the body so the hand stays closed wherever the pointer goes.
      document.body.style.cursor = "grabbing";
      const onMove = (ev) => {
        if (!panOrigin.current) return;
        setViewport((v) => ({
          ...v,
          x: panOrigin.current.x + (ev.clientX - panOrigin.current.px),
          y: panOrigin.current.y + (ev.clientY - panOrigin.current.py),
        }));
      };
      const onUp = () => {
        panOrigin.current = null;
        document.body.style.removeProperty("cursor");
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [viewport.x, viewport.y]
  );

  return {
    viewport,
    setViewport,
    toBoard,
    zoomAt,
    zoomTo,
    zoomBy,
    panBy,
    fitTo,
    reset,
    startPan,
    MIN_ZOOM,
    MAX_ZOOM,
  };
}
